<template>
  <div class="spinners-item d-flex flex-column">
    <v-tooltip
      top color="grey800"
      opacity="1"
      max-width="200"
    >
      <template #activator="{on}">
        <fai
          :icon="['fad', 'info-circle']"
          fixed-width
          class="fairness-icon blue200--text link ma-2 text-body-1"
          v-on="on"
        />
      </template>

      <div class="py-2">
        <div class="field mb-1">
          <h5 v-t="'common.hash'" class="text-caption text-uppercase font-weight-bold blue300--text" />
          <p class="mb-0 text-caption text--secondary text-break lh-1-2" v-text="game.fairness.hash" />
        </div>

        <div class="field mb-1">
          <h5 v-t="'common.eos_block'" class="text-caption text-uppercase font-weight-bold blue300--text" />
          <p
            class="mb-0 text-caption text--secondary text-break lh-1-2"
            v-text="`#${game.fairness.solvingBlockId || '...'}`"
          />
        </div>

        <div v-show="game.fairness.secret" class="field">
          <h5 v-t="'common.secret'" class="text-caption text-uppercase font-weight-bold blue300--text" />
          <p class="mb-0 text-caption text--secondary text-break lh-1-2" v-text="game.fairness.secret" />
        </div>
      </div>
    </v-tooltip>

    <scrap class="game-total text-body-2 my-3 mx-2" :amount="game.amount * slots.length" />

    <div class="game-wheel py-5 d-flex align-center justify-center">
      <SpinnersWheel :size="112" :game="game" :conga="conga" class="flex-grow-0" />
    </div>

    <div class="player-slots flex-grow-1 pa-1">
      <div
        v-for="color in slots" :key="color"
        class="slot d-flex align-center px-2 py-1 text-body-2"
        :class="{empty: !playersByColor[color]}"
        @click="onClickSlot(color)"
      >
        <fai :icon="['fas', 'square']" class="mr-2 text-body-1" :style="{color: colorStyles[color]}" />
        <div
          v-if="playersByColor[color]"
          class="d-flex text-truncate align-center text-body-2 link"
          @click="$userMenu.open($event, playersByColor[color])"
        >
          <user-avatar size="22" class="mr-2" :user="playersByColor[color]" />
          <username class="grey100--text" :user="playersByColor[color]" />
        </div>
        <span v-else-if="canJoin" class="text--secondary text-body-2 font-weight-light">
          <span v-t="'cta.join'" />
          <scrap :amount="game.amount" class="text-caption" />
        </span>
        <span v-else-if="isCreator" class="text--secondary text-body-2">
          <fai :icon="['fad', 'robot']" class="blue200--text mr-1" />
          Call a bandit
        </span>
        <span v-else class="text--disabled text-caption">
          Waiting for player
        </span>
      </div>
    </div>

    <div class="game-actions d-flex align-center">
      <v-btn
        v-show="canCancel"
        v-t="'cta.cancel'" depressed
        block
        color="primary700"
        class="primary200--text text-caption rounded-0"
        :ripple="false"
        @click="$emit('expire', game.id)"
      />

      <v-btn
        v-show="!canCancel"
        depressed block
        color="green700"
        class="green200--text text-caption rounded-0"
        :ripple="false"
        :disabled="!canJoin"
        @click="onJoinRandomSlot"
        v-text="joinButtonText"
      />
    </div>
  </div>
</template>

<script>
import { GAME_COLORS, COLOR_HEXCODES, GAME_STATUS } from '../utils'
import SpinnersWheel from './SpinnersWheel'

export default {
  components: {
    SpinnersWheel,
  },
  props: {
    game: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    conga: {
      type: Object,
      required: true,
    },
  },
  computed: {
    colorStyles() {
      return COLOR_HEXCODES
    },
    slots() {
      const { slots } = this.game
      return GAME_COLORS.slice(0, slots)
    },
    players() {
      return this.game.players
    },
    playersByColor() {
      return Object.values(this.players).reduce((acc, player) => {
        acc[player.color] = player
        return acc
      }, {})
    },
    isCreator() {
      return this.game.data.creator === this.user?.steamid
    },
    canCancel() {
      if (!this.isCreator) return false
      return this.game.status === GAME_STATUS.open
    },
    canJoin() {
      if (this.players[this.user?.steamid]) return false

      return Object.keys(this.players).length < this.slots.length
    },
    joinButtonText() {
      if (this.game.status === GAME_STATUS.locked) {
        const { solvingBlockId } = this.game.fairness
        return this.$tc('page.spinners.status.awaiting_eos', solvingBlockId)
      }

      if (this.game.status === GAME_STATUS.complete) return this.$t('page.spinners.status.rolled')
      if (this.canJoin) return this.$t('cta.join_game')
      if (this.players[this.user?.steamid]) return this.$t('page.spinners.status.joined')

      return this.$t('page.spinners.status.game_full')
    },
  },
  methods: {
    onClickSlot(color) {
      if (this.playersByColor[color]) return

      if (this.isCreator) {
        this.$emit('request-bot', this.game.id, color)
      } else if (this.canJoin) {
        this.$emit('join', this.game.id, color)
      }
    },
    onJoinRandomSlot() {
      const { slots, playersByColor } = this
      const color = slots.find(i => !playersByColor[i])
      this.$emit('join', this.game.id, color)
    },
  },
}
</script>

<style lang="scss" scoped>
.spinners-item {
  overflow: hidden;
  position: relative;
  background-color: rgba(#6e6b65, 0.55);

  .fairness-icon {
    position: absolute;
    top: 0;
    right: 0;
  }

  .game-total {
    position: absolute;
    top: 0;
    left: 0;
  }

  .game-wheel {
    background-color: rgba(#6e6b65, 0.2);
  }

  .game-actions {
    background-color: rgba(#6e6b65, 0.2);
  }

  .player-slots {
    .slot {
      &.empty {
        cursor: pointer;
        transition: opacity 0.3s ease;

        &:hover {
          opacity: 0.8;
          background-color: rgba(#6e6b65, 0.25);
        }
      }
    }
  }
}
</style>
